import { Injectable } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { ResponseWrapper } from '@library/common/http';

import { BehaviorSubject, Observable, Subject, pipe } from 'rxjs';
import { map, tap, switchMap } from 'rxjs/operators';

import { AppConfigService, AppConfigProperty } from '@library/app.config';
import { Business } from '@library/common/models';
import { CacheService } from '@library/core/services';

@Injectable({
   providedIn: 'root',
})
export class AddressbookService {

   url = 'ab';

   refresh$: Subject<void> = new Subject();

   lengths: AppConfigProperty;

   filter$ = new BehaviorSubject( 'all' );

   constructor(
      private appConfig: AppConfigService,
      private http: HttpClient,
      private cache: CacheService,
   ) {
      this.lengths = this.appConfig.getProperty( this.appConfig.config.booking, 'maxLength' );
   }

   getAddressBook(): Observable<Business[]> {
      return this.cache.request<ResponseWrapper<Business>, Business[]>({
         url: this.url,
         pipe: pipe(
            map( rw => rw.response.collection ),
            map(( ab: Business[] ) => ab.map( a => new Business( a ) )),
            map( ab => ab.sort(( a, b ) => a.name.toLowerCase().charCodeAt( 0 ) - b.name.toLowerCase().charCodeAt( 0 ))),
         ),
         refresher: this.refresh$,
         useLambda: true,
      });
   }

   getBusiness( id: number ): Observable<Business> {
      return this.getAddressBook().pipe(
         map( ab => ab.filter( a => a.id === id )[0] ),
      );
   }

   upsertBusiness( business: Business ): Observable<Business> {
      business.name = business.name.trim();
      business.address1 = business.address1.trim();

      return this.http.post<ResponseWrapper<Business>>( this.url, business, { params: { useLambda: true } } )
         .pipe(
            tap( _ => this.expire() ),
            switchMap( _ => this.getAddressBook() ),
            map( ab => ab.filter( b => business.id ? b.id === business.id : b.name === business.name )),
            map( ab => ab.sort(( a, b ) => a.id - b.id )[0] ),
         );
   }

   addBusiness( business: Business ): Observable<Business> {
      business = new Business( business ).save();

      return this.http.post<ResponseWrapper<Business>>( this.url, business )
         .pipe(
            tap( _ => this.expire() ),
            switchMap( _ => this.getAddressBook() ),
            map( ab => ab.filter( b => b.name === business.name )),
            map( ab => ab.sort(( a, b ) => a.id - b.id )[0] ),
         );
   }

   deleteBusiness( id: number ): Observable<Business[]> {
      return this.http.delete<ResponseWrapper<Business>>( `${this.url}/${id}` , { params: { useLambda: true } } )
         .pipe(
            tap( _ => this.expire() ),
            switchMap( _ => this.getAddressBook() ),
         );
   }

   expire() {
      this.reset();
   }

   reset() {
      this.cache.clearSet( this.url );
      this.refresh$.next();
   }

   changeFilter( filter: string ) {
      this.filter$.next( filter );
   }
}
